import { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { Button } from '../../ui/Button';

function formatCredentialsText(credentials, enrolledServices = []) {
  const lines = [
    'HMRC Sandbox Test User Credentials',
    '==================================',
    '',
    `User ID (Government Gateway): ${credentials.userId ?? '—'}`,
    `Password: ${credentials.password ?? '—'}`,
    `Full name: ${credentials.userFullName ?? '—'}`,
    `Email: ${credentials.emailAddress ?? '—'}`,
  ];

  if (credentials.vrn) lines.push(`VRN: ${credentials.vrn}`);
  if (credentials.nino) lines.push(`NINO: ${credentials.nino}`);
  if (credentials.mtdItId) lines.push(`MTD IT ID: ${credentials.mtdItId}`);
  if (credentials.saUtr) lines.push(`SA UTR: ${credentials.saUtr}`);
  if (credentials.eoriNumber) lines.push(`EORI: ${credentials.eoriNumber}`);

  if (enrolledServices.length > 0) {
    lines.push('', `Enrolled services: ${enrolledServices.join(', ')}`);
  }

  if (credentials.organisationDetails) {
    lines.push('', 'Organisation details:', JSON.stringify(credentials.organisationDetails, null, 2));
  }
  if (credentials.individualDetails) {
    lines.push('', 'Individual details:', JSON.stringify(credentials.individualDetails, null, 2));
  }

  return lines.join('\n');
}

function CredentialRow({ label, value, mono = false }) {
  if (value == null || value === '') return null;

  return (
    <div>
      <dt className="text-[var(--ds-text-muted)] text-micro">{label}</dt>
      <dd className={`font-medium text-[var(--ds-text-primary)] ${mono ? 'font-mono text-xs break-all' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

export default function HmrcSandboxCredentialsPanel({ credentials, enrolledServices = [], onDismiss }) {
  const [feedback, setFeedback] = useState(null);

  if (!credentials) return null;

  const showFeedback = (message, isError = false) => {
    setFeedback({ message, isError });
    setTimeout(() => setFeedback(null), 2000);
  };

  const handleCopy = async () => {
    try {
      const text = formatCredentialsText(credentials, enrolledServices);
      await navigator.clipboard.writeText(text);
      showFeedback('Copied to clipboard');
    } catch {
      showFeedback('Could not copy to clipboard', true);
    }
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(credentials, null, 2));
      showFeedback('JSON copied to clipboard');
    } catch {
      showFeedback('Could not copy JSON to clipboard', true);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(credentials, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `hmrc-sandbox-user-${timestamp}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showFeedback('Download started');
  };

  return (
    <div className="space-y-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-status-success)]/40 bg-[var(--ds-status-success)]/5 p-4">
      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-micro text-amber-800 dark:text-amber-200">
        These credentials are shown once and are not stored by SparxMate. Save them now — you will not be able to
        retrieve the password later.
      </div>

      <h4 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Created sandbox user</h4>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-micro">
        <CredentialRow label="User ID (Government Gateway)" value={credentials.userId} mono />
        <CredentialRow label="Password" value={credentials.password} mono />
        <CredentialRow label="Full name" value={credentials.userFullName} />
        <CredentialRow label="Email" value={credentials.emailAddress} />
        <CredentialRow label="VRN" value={credentials.vrn} mono />
        <CredentialRow label="NINO" value={credentials.nino} mono />
        <CredentialRow label="MTD IT ID" value={credentials.mtdItId} mono />
        <CredentialRow label="SA UTR" value={credentials.saUtr} mono />
        <CredentialRow label="EORI" value={credentials.eoriNumber} mono />
        {enrolledServices.length > 0 && (
          <div className="md:col-span-2">
            <dt className="text-[var(--ds-text-muted)] text-micro">MTD enrolments</dt>
            <dd className="font-mono text-xs text-[var(--ds-text-primary)]">{enrolledServices.join(', ')}</dd>
          </div>
        )}
        {credentials.organisationDetails && (
          <div className="md:col-span-2">
            <dt className="text-[var(--ds-text-muted)] text-micro mb-1">Organisation details</dt>
            <dd className="font-mono text-xs text-[var(--ds-text-primary)] whitespace-pre-wrap break-all">
              {JSON.stringify(credentials.organisationDetails, null, 2)}
            </dd>
          </div>
        )}
        {credentials.individualDetails && (
          <div className="md:col-span-2">
            <dt className="text-[var(--ds-text-muted)] text-micro mb-1">Individual details</dt>
            <dd className="font-mono text-xs text-[var(--ds-text-primary)] whitespace-pre-wrap break-all">
              {JSON.stringify(credentials.individualDetails, null, 2)}
            </dd>
          </div>
        )}
      </dl>

      <div className="flex flex-wrap gap-2 items-center">
        <Button type="button" variant="outline" size="sm" onClick={handleCopy} className="gap-1">
          <Copy className="h-4 w-4" />
          Copy text
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleCopyJson} className="gap-1">
          <Copy className="h-4 w-4" />
          Copy JSON
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleDownload} className="gap-1">
          <Download className="h-4 w-4" />
          Download JSON
        </Button>
        {onDismiss && (
          <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
        {feedback && (
          <span
            className={`text-micro ${feedback.isError ? 'text-[var(--ds-status-danger)]' : 'text-[var(--ds-status-success)]'}`}
          >
            {feedback.message}
          </span>
        )}
      </div>
    </div>
  );
}
